import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Spacer } from '@kerfjs/ui/spacer';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';
import { ShieldCheck } from 'lucide';

import type { VerdictRowT } from '../data/api.js';
import { formatPercentText } from '../lib/currency-format.js';
import { impactOf } from '../lib/helpers.js';
import { CurrencyAmount } from './currency-amount.js';
import { LINE_ITEM_SECTION_VALUES_FLEX, LineItemSectionLabel } from './line-item-section-label.js';
import { RangeBar } from './range-bar.js';
import { num } from './ui-helpers.js';
import { VerdictChip } from './verdict-chip.js';

export function DemandLineItemAnalysis({
  verdict,
  base,
  quantity,
  lineId,
  hs,
}: {
  verdict: VerdictRowT | undefined;
  base: string;
  quantity: string | null;
  lineId: string;
  hs?: { code: string | null; dutyPct: string | null; confirmed: boolean };
}): SafeHtml {
  if (verdict === undefined) return <></>;
  const impact = impactOf(verdict, Number(quantity ?? 1) || 1);
  return (
    <List className="demand-line-item-analysis">
      <Row className="demand-line-item-analysis-verdict" vAlign="top" wrap>
        <LineItemSectionLabel label="AI Analysis" />
        <Row flex={LINE_ITEM_SECTION_VALUES_FLEX} wrap vAlign="middle" controlInsets="rl">
          <ListInsetControl sides="tb">
            <VerdictChip verdict={verdict.verdict} />
          </ListInsetControl>
          <RangeBar
            low={num(verdict.benchmarkLow)}
            high={num(verdict.benchmarkHigh)}
            best={num(verdict.bestQuotePrice)}
            base={base}
          />
          {verdict.deltaPct !== null ? (
            <Text>{formatPercentText(Number(verdict.deltaPct) / 100, { signed: true })}</Text>
          ) : null}
          {verdict.verdict === 'estimate' && num(verdict.targetPrice) !== null ? (
            // An estimate names the highest quote that would still be fair (HS2-AX3JJY).
            <Text>
              Fair up to <CurrencyAmount value={num(verdict.targetPrice) ?? 0} currency={base} />
            </Text>
          ) : verdict.recommendedAction !== null && verdict.recommendedAction !== '' ? (
            <Text>{verdict.recommendedAction}</Text>
          ) : null}

          <Row flex vAlign="middle">
            <Spacer flex />

            {impact > 0 ? (
              <Text>
                <CurrencyAmount value={impact} currency={base} />
              </Text>
            ) : null}

            <Button
              tone="neutral"
              size="compact"
              emphasis="plain"
              action="line-evidence-open"
              ariaLabel="View Evidence…"
              title="View evidence…"
              rootAttributes={{ 'data-line-id': lineId }}
              icon={<LucideIcon icon={ShieldCheck} name="" />}
            />
          </Row>
        </Row>
      </Row>
      {hs !== undefined && hs.code !== null && hs.code !== '' ? (
        <Row className="demand-line-item-hscode" vAlign="top" wrap>
          <LineItemSectionLabel label="HS Code" />
          <Row flex={LINE_ITEM_SECTION_VALUES_FLEX} wrap vAlign="middle" controlInsets="rl">
            <Text font="monospace">
              <strong>{hs.code}</strong>
            </Text>
            {hs.dutyPct !== null ? (
              <Text tone="quiet" size="compact">
                duty {formatPercentText(Number(hs.dutyPct) / 100)}
              </Text>
            ) : null}
            {hs.confirmed ? (
              <ListInsetControl sides="tb">
                <Badge tone="success" appearance="solid" shape="pill">
                  confirmed
                </Badge>
              </ListInsetControl>
            ) : (
              <>
                <ListInsetControl sides="tb">
                  <Badge tone="info" appearance="solid" shape="pill">
                    AI-proposed
                  </Badge>
                </ListInsetControl>
                <Spacer flex />
                <ListInsetControl sides="tb">
                  <Button
                    size="compact"
                    tone="brand"
                    emphasis="primary"
                    action="confirm-hs-code"
                    rootAttributes={{ 'data-line-id': lineId }}
                  >
                    Confirm HS Code
                  </Button>
                </ListInsetControl>
              </>
            )}
          </Row>
        </Row>
      ) : null}
    </List>
  );
}
